import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { CommonApiService } from '../../../common-api.service';
import { CommonDropdownService } from '../../../common-dropdown.service';
import { SpeakerService } from '../../../speaker.service';
import { SnackbarService } from '../../../snackbar.service';
import { environment } from '../../../../../environments/environment';
import { tap, filter, finalize } from 'rxjs/operators';
import { MonthOption, buildMonthOptions, findMonthOption } from '../../../util/month-options.util';

@Component({
  selector: 'app-sales-comm-person-report',
  templateUrl: './sales-comm-person-report.component.html',
  styleUrls: ['./sales-comm-person-report.component.css'],
})
export class SalesCommPersonReportComponent implements OnInit, OnDestroy {
  // ── Person type ─────────────────────────────────────────────────────────────
  personType: 'sales' | 'communication' | 'both' = 'sales';
  reportType: 'summary' | 'product_wise_summary' = 'product_wise_summary';

  monthOptions: MonthOption[] = [];
  selectedFromMonthValue = '';
  selectedToMonthValue = '';

  // ── Person dropdown (paginated) ──────────────────────────────────────────────
  @ViewChild('personSelect', { static: false }) personSelect: MatSelect;

  allPersons: any[] = [];
  allPersonsCount = 0;
  personSearchPage = 0;
  selectedPersonId = 0;
  selectedPersonObject: any[] = [];

  // ── Display config ───────────────────────────────────────────────────────────
  private readonly productWiseColumns = [
    'invoice_no', 'doctor_name', 'patient_name', 'city', 'area', 'month',
    'marketing_person', 'communication_person',
    'total_jobs', 'product', 'unit', 'per_unit',
    'amt', 'cgst', 'sgst', 'igst', 'total_amt',
  ];
  private readonly productWiseHeaders = [
    'Invoice No', 'Dr Name', 'Patient Name', 'City', 'Area', 'Month',
    'Marketing Person', 'Communication Person',
    'Total No. of Job', 'Product', 'Unit', 'Per Unit',
    'Amt', 'CGST', 'SGST', 'IGST', 'Total Amt',
  ];
  private readonly summaryColumns = [
    'invoice_no', 'doctor_name', 'city', 'area', 'month',
    'marketing_person', 'communication_person',
    'total_jobs', 'unit', 'amt', 'cgst', 'sgst', 'igst', 'total_amt',
  ];
  private readonly summaryHeaders = [
    'Invoice No', 'Dr Name', 'City', 'Area', 'Month',
    'Marketing Person', 'Communication Person',
    'Total No. of Job', 'Unit', 'Amt', 'CGST', 'SGST', 'IGST', 'Total Amt',
  ];
  displayColumns = this.productWiseColumns;
  displayHeaders = this.productWiseHeaders;

  constructor(
    public speaker: SpeakerService,
    private api: CommonApiService,
    private dropdownService: CommonDropdownService,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.speaker.listData = [];
    this.monthOptions = buildMonthOptions(60).map((option) => ({
      ...option,
      label: this._monthLabel(option),
    }));
    this.selectedFromMonthValue = this.monthOptions[0]?.value || '';
    this.selectedToMonthValue = this.monthOptions[0]?.value || '';
    this.selectedPersonId = 0;
    this.onLoadPersonList();
  }

  ngOnDestroy(): void {
    this.speaker.exportBackend = null;
  }

  private _fmt(d: Date): string {
    const yy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }

  private _monthLabel(option: MonthOption): string {
    const monthDate = new Date(option.year, option.month - 1, 1);
    return monthDate.toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric',
    });
  }

  private _monthStart(option: MonthOption): string {
    return this._fmt(new Date(option.year, option.month - 1, 1));
  }

  private _monthEnd(option: MonthOption): string {
    return this._fmt(new Date(option.year, option.month, 0));
  }

  get selectedFromMonthLabel(): string {
    return findMonthOption(this.monthOptions, this.selectedFromMonthValue)?.label || '';
  }

  get selectedToMonthLabel(): string {
    return findMonthOption(this.monthOptions, this.selectedToMonthValue)?.label || '';
  }

  get selectedReportTypeLabel(): string {
    return this.reportType === 'summary' ? 'Summary' : 'Product Wise Summary';
  }

  get selectedPersonName(): string {
    if (this.selectedPersonId === 0) return 'All';
    return this.selectedPersonObject[0]?.name || '';
  }

  // ── Person dropdown handlers ─────────────────────────────────────────────────
  onLoadPersonList(): void {
    const params = {
      pageIndex: this.personSearchPage,
      pageSize: environment.defaultPageSizeOnScroll,
    };
    this.api.postAll('employee/list/display', params).pipe(
      tap((res: any) => {
        this.allPersons.push(...res.items);
        this.allPersonsCount = res.rowCount;
      }),
      filter(() => this.selectedPersonObject.length !== 0),
      tap(() => {
        const selId = this.selectedPersonObject[0].id;
        this.allPersons = this.allPersons.filter((e) => e.id !== selId);
        this.allPersons.unshift(this.selectedPersonObject[0]);
      }),
      finalize(() => this.personSearchPage++),
    ).subscribe();
  }

  onPersonKey(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dropdownService
      .updateSearchResults(value, 'employee', this.allPersons, this.selectedPersonObject, 'personSearchPage')
      .subscribe((result: { entityList: any[]; pageVariable: number }) => {
        this.allPersons = result.entityList;
        this.personSearchPage = result.pageVariable;
      });
  }

  onPersonScroll(event: any): void {
    if (!this.personSelect?.panel || !this.personSelect?.panel.nativeElement) return;
    if (event) {
      this.personSelect.panel.nativeElement.addEventListener('scroll', () => {
        const el = this.personSelect.panel.nativeElement;
        const tolerance = 10;
        if (
          Math.floor(el.scrollTop) + tolerance >=
          Math.floor(el.scrollHeight) - Math.floor(el.offsetHeight)
        ) {
          if (this.allPersons.length >= this.allPersonsCount) return;
          const params = {
            pageIndex: this.personSearchPage,
            pageSize: environment.defaultPageSizeOnScroll,
          };
          this.api.postAll('employee/list/display', params).subscribe((res: any) => {
            res.items = res.items.filter((e: any) => e.id !== this.selectedPersonObject[0]?.id);
            this.allPersons.push(...res.items);
          });
          this.personSearchPage++;
        }
      });
    }
  }

  onPersonTypeChange(): void {
    this.selectedPersonId = 0;
    this.selectedPersonObject = [];
    this.speaker.listData = [];
    this.speaker.exportBackend = null;
  }

  onReportTypeChange(): void {
    this.speaker.listData = [];
    this.speaker.exportBackend = null;
    this.applyDisplayConfig();
  }

  onPersonSelected(personId: number): void {
    if (personId === 0) {
      this.selectedPersonObject = [];
      return;
    }

    const selectedPerson = this.allPersons.find((person) => person.id === personId);
    this.selectedPersonObject = selectedPerson ? [selectedPerson] : [];
  }

  private applyDisplayConfig(): void {
    if (this.reportType === 'summary') {
      this.displayColumns = this.summaryColumns;
      this.displayHeaders = this.summaryHeaders;
      return;
    }

    this.displayColumns = this.productWiseColumns;
    this.displayHeaders = this.productWiseHeaders;
  }

  // ── Generate report ──────────────────────────────────────────────────────────
  generateReport(): void {
    const fromMonth = findMonthOption(this.monthOptions, this.selectedFromMonthValue);
    const toMonth = findMonthOption(this.monthOptions, this.selectedToMonthValue);

    if (!fromMonth || !toMonth) {
      this.snackbar.warning('Please select a month range');
      return;
    }

    const fromDate = this._monthStart(fromMonth);
    const toDate = this._monthEnd(toMonth);

    if (new Date(fromDate).getTime() > new Date(toDate).getTime()) {
      this.snackbar.warning('From month must be on or before To month');
      return;
    }

    const payload = {
      person_type: this.personType,
      report_type: this.reportType,
      person_id: this.selectedPersonId === 0 ? null : this.selectedPersonId,
      person_name: this.selectedPersonName,
      from_date: fromDate,
      to_date: toDate,
    };

    this.speaker.listData = [];
    this.speaker.isLoading = true;

    this.api.callRoute('doctor', '/person_report', payload).subscribe({
      next: (res: any[]) => {
        this.speaker.isLoading = false;
        this.applyDisplayConfig();
        this.speaker.listData = res || [];
        this.speaker.model = 'doctor';
        this.speaker.displayColumns = this.displayColumns;
        this.speaker.displayHeaders = this.displayHeaders;
        this.speaker.exportBackend = {
          model: 'doctor',
          route: '/person_report/export',
          payload,
          filename: `${this.selectedPersonName} ${this.selectedReportTypeLabel} Report - ${this.selectedFromMonthLabel} to ${this.selectedToMonthLabel}.xlsx`,
        };
        if (!res || res.length === 0) {
          this.snackbar.info('No records found for the selected criteria');
        }
      },
      error: () => {
        this.speaker.isLoading = false;
        this.snackbar.error('Failed to load report');
      },
    });
  }
}

